import { createContext, useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export interface PageMeta {
  title: string;
  subtitle?: string;
}

/** The meta plus the path that set it, so a page's leftover meta never shows on the next route. */
export interface StoredPageMeta extends PageMeta {
  path: string;
}

export const PageMetaValueContext = createContext<StoredPageMeta | null>(null);
export const PageMetaSetContext = createContext<(meta: StoredPageMeta | null) => void>(() => {});

/**
 * A detail page's TopBar title/subtitle, set while it is mounted (one caller per page).
 * `null` (e.g. while loading) keeps the route's default.
 */
export function usePageMeta(meta: PageMeta | null): void {
  const setMeta = useContext(PageMetaSetContext);
  const { pathname } = useLocation();
  const title = meta?.title;
  const subtitle = meta?.subtitle;

  useEffect(() => {
    if (title === undefined) return;
    setMeta({ path: pathname, title, subtitle });
    return () => setMeta(null);
  }, [setMeta, pathname, title, subtitle]);
}

/** Layout's side: the meta the current route's page set, if any. */
export function useCurrentPageMeta(): PageMeta | null {
  const meta = useContext(PageMetaValueContext);
  const { pathname } = useLocation();
  return meta && meta.path === pathname ? meta : null;
}
